/**
 * @fileoverview Utility per la gestione FormData - Migrazione TypeScript
 *
 * Servizi per la creazione e manipolazione di oggetti FormData
 * per upload di file e invio di dati complessi via HTTP.
 *
 * @author Generated by TypeScript Migration
 * @since 2025-09-28
 */

/**
 * Tipo generico per oggetti che possono essere serializzati in FormData
 */
type SerializableValue = string | number | boolean | Date | File | Blob | null | undefined;

/**
 * Interfaccia per modelli serializzabili in FormData
 */
interface SerializableModel {
	[key: string]: SerializableValue | SerializableValue[] | SerializableModel | SerializableModel[];
}

/**
 * Create a FormData object starting from a raw file taken from a FileUploader
 *
 * @param rawFile - The file taken from the file upload component
 * @returns FormData object containing the file
 * @throws Error if rawFile is null or undefined
 *
 * @example
 * ```typescript
 * const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
 * const file = fileInput.files?.[0];
 * const formData = bindFormDataFromFile(file);
 * ```
 */
export const bindFormDataFromFile = (rawFile: File | null | undefined): FormData => {
	if (!rawFile) {
		throw new Error("The File can't be null!");
	}

	const ret = new FormData();
	ret.append('file', rawFile);

	return ret;
};

/**
 * Add a series of properties to a FormData based on the input model object
 *
 * Recursively serializes complex objects, arrays, and nested structures
 * into FormData format suitable for multipart/form-data HTTP requests.
 *
 * @param model - The object containing all properties to append to FormData
 * @param form - Optional existing FormData to append to (creates new if null)
 * @param namespace - Optional namespace prefix for nested property names
 * @returns FormData object with all model properties appended
 *
 * @example
 * ```typescript
 * const model = {
 *   name: 'John',
 *   age: 30,
 *   address: { city: 'Rome', country: 'IT' },
 *   tags: ['developer', 'typescript']
 * };
 * const formData = appendPropertyByModel(model);
 * // Results in FormData with:
 * // name: 'John'
 * // age: '30'
 * // address[city]: 'Rome'
 * // address[country]: 'IT'
 * // tags[0]: 'developer'
 * // tags[1]: 'typescript'
 * ```
 */
export const appendPropertyByModel = (
	model: SerializableModel,
	form: FormData | null = null,
	namespace = '',
): FormData => {
	const formData = form || new FormData();

	for (const propertyName in model) {
		if (!Object.prototype.hasOwnProperty.call(model, propertyName) || !model[propertyName]) {
			continue;
		}

		const value = model[propertyName];
		const formKey = namespace ? `${namespace}[${propertyName}]` : propertyName;

		if (value instanceof Date) {
			formData.append(formKey, value.toISOString());
		} else if (Array.isArray(value)) {
			value.forEach((element, index) => {
				const tempFormKey = `${formKey}[${index}]`;
				if (typeof element === 'object' && element !== null && !(element instanceof File)) {
					appendPropertyByModel(element as SerializableModel, formData, tempFormKey);
				} else if (element !== null && element !== undefined) {
					formData.append(tempFormKey, String(element));
				}
			});
		} else if (typeof value === 'object' && value !== null && !(value instanceof File)) {
			appendPropertyByModel(value as SerializableModel, formData, formKey);
		} else if (value !== null && value !== undefined) {
			formData.append(formKey, String(value));
		}
	}

	return formData;
};

/**
 * Create a complete FormData object for file upload with additional model data
 *
 * Combines file upload with additional form fields in a single FormData object
 * suitable for multipart/form-data POST requests.
 *
 * @param rawFile - The file to upload
 * @param model - Additional data to include with the file upload
 * @returns FormData containing both file and model properties
 *
 * @example
 * ```typescript
 * const file = fileInput.files?.[0];
 * const metadata = {
 *   description: 'Profile picture',
 *   category: 'avatar',
 *   userId: 123
 * };
 * const formData = bindFormData(file, metadata);
 * // Ready for: await api.post('/upload', formData)
 * ```
 */
export const bindFormData = (
	rawFile: File | null | undefined,
	model: SerializableModel,
): FormData => appendPropertyByModel(model, bindFormDataFromFile(rawFile));
